import type { DeviceData, IotCoreData } from '@/types/devices'
import { getLatestTimestamp } from '@/helpers/common/helper'
import { subscriptionTypes } from '@/store/subscriptions/utils'

const DAY_MS = 24 * 60 * 60 * 1000
const HOUR_MS = 60 * 60 * 1000
const DISCONNECTED_DEVICE_MILLISECONDS = 5 * 60 * 1000

export const activeLastDay = (date: string | number): boolean => {
  return wasActiveWithinTime(date, DAY_MS)
}

export const activeLastHour = (date: string | number): boolean => {
  return wasActiveWithinTime(date, HOUR_MS)
}

export const wasActiveWithinTime = (date: string | number, timeInMilliseconds: number): boolean => {
  if (date && timeInMilliseconds && Number.isInteger(timeInMilliseconds)) {
    const lastContacted = new Date(date)
    if (isNaN(lastContacted.getTime())) {
      return false
    }
    return new Date().getTime() - lastContacted.getTime() < timeInMilliseconds
  }
  return false
}

export const validateTimestamp = (timestamp: string, noTS: string): string => {
  return new Date(timestamp).getTime() > 0 ? timestamp : noTS
}

export const isDeviceConnected = (lastCommunicationTime: string | null): boolean => {
  if (lastCommunicationTime) {
    const lastCommunicationDate = new Date(lastCommunicationTime)
    if (isNaN(lastCommunicationDate.getTime())) {
      return false
    }
    return lastCommunicationDate.getTime() > new Date().getTime() - DISCONNECTED_DEVICE_MILLISECONDS
  }
  return false
}

export const getDeviceSubscriptionType = (device: DeviceData): string => {
  const isShared = device?.is_shared
  const hasDataServices = device?.activations?.dataservices !== undefined
  const websiteSubscription = device?.activations?.website?.subType

  if (isShared) {
    return subscriptionTypes.NOT_APPLICABLE
  }

  const hasTsiLinkPremium = websiteSubscription === subscriptionTypes.WEBSITE_PREMIUM
  const hasTsiLinkBasic = websiteSubscription === subscriptionTypes.WEBSITE_BASIC
  const hasDeviceSmartBridge = websiteSubscription === subscriptionTypes.DEVICE_SMART_BRIDGE
  const hasStudies = websiteSubscription === subscriptionTypes.STUDIES

  switch (true) {
    case hasDataServices && (hasTsiLinkBasic || hasTsiLinkPremium):
      return subscriptionTypes.DUAL
    case hasDataServices:
      return subscriptionTypes.DATA_SERVICES
    case hasTsiLinkPremium:
      return subscriptionTypes.WEBSITE_PREMIUM
    case hasTsiLinkBasic:
      return subscriptionTypes.WEBSITE_BASIC
    case hasDeviceSmartBridge:
      return subscriptionTypes.DEVICE_SMART_BRIDGE
    case hasStudies:
      return subscriptionTypes.STUDIES
    default:
      return subscriptionTypes.NOT_APPLICABLE
  }
}

export const getLastDeviceCommunicationTimestamp = (iotCoreData: IotCoreData): string | null => {
  if (iotCoreData && typeof iotCoreData === 'object') {
    return getLatestTimestamp([
      iotCoreData.last_event_time,
      iotCoreData.last_heartbeat_time,
      iotCoreData.last_state_time
    ])
  }
  return null
}

export const getLastConnectedDeviceCommunicationTimestamp = (
  lastTelemetryTimestamp: string,
  lastStateTimestamp: string
): string | null => {
  return getLatestTimestamp([lastTelemetryTimestamp, lastStateTimestamp])
}
